"use client";

import { RiSearchLine } from "@remixicon/react";
import type {
	ArtistSearchResult,
	FestivalSearchResult,
	SearchResponse,
	ShowSearchResult,
	TopNavSearchProps,
	UserSearchResult,
} from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useEffect } from "react";
import { SearchDialog } from "@/components/features/search";
import { useSearch, useSearchState } from "@/hooks/features/search";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import {
	trackArtistDiscovery,
	trackSearchResultClick,
} from "@/lib/services/analytics";

export function TopNavSearch({
	onNavigateToFestival,
	useSearchState: useEnhancedSearchState,
	useSearchHook: useEnhancedSearchHook,
}: TopNavSearchProps) {
	// Always call both hooks to satisfy React's rules
	const defaultSearchState = useSearchState();
	const enhancedSearchState = useEnhancedSearchState?.();

	// Use enhanced state if provided, otherwise use default
	const searchState = enhancedSearchState || defaultSearchState;
	const {
		isSearchOpen,
		searchValue,
		selectedSearchIndex,
		setSearchOpen,
		setSearchValue,
		setSelectedSearchIndex,
		resetSearchState,
	} = searchState;

	// Always call both search hooks
	const defaultSearchQuery = useSearch(searchValue, {
		enabled: isSearchOpen && !useEnhancedSearchHook,
	});
	const enhancedSearchQuery = useEnhancedSearchHook?.(searchValue, {
		enabled: isSearchOpen,
	});

	// Use enhanced query if available, otherwise use default
	const searchQuery = enhancedSearchQuery || defaultSearchQuery;

	// Extract data from the query result
	const { data: searchData, isLoading = false, error } = searchQuery;

	const {
		users: userResults = [],
		festivals: festivalResults = [],
		artists: artistResults = [],
		shows: showResults = [],
	} = (searchData as SearchResponse | undefined) || {};

	// Calculate total results
	const totalResults =
		userResults.length +
		festivalResults.length +
		artistResults.length +
		showResults.length;

	// Auto-select first result when results appear
	useEffect(() => {
		if (
			totalResults > 0 &&
			selectedSearchIndex === -1 &&
			searchValue.trim().length > 0
		) {
			setSelectedSearchIndex(0);
		} else if (totalResults === 0 && selectedSearchIndex !== -1) {
			setSelectedSearchIndex(-1);
		}
	}, [totalResults, selectedSearchIndex, searchValue, setSelectedSearchIndex]);

	// Helper function to get position of a user in results
	const getUserPosition = useCallback(
		(username: string) => {
			const userIndex = userResults.findIndex(
				(u: UserSearchResult) => u.username === username,
			);
			if (userIndex === -1) return -1;
			// Users come after festivals, shows, and artists
			return (
				festivalResults.length +
				showResults.length +
				artistResults.length +
				userIndex
			);
		},
		[userResults, festivalResults, showResults, artistResults],
	);

	// Helper function to get position of a festival in results
	const getFestivalPosition = useCallback(
		(festivalSlug: string) => {
			return festivalResults.findIndex(
				(f: FestivalSearchResult) => f.slug === festivalSlug,
			);
		},
		[festivalResults],
	);

	// Show position tracking is disabled until show analytics support is added

	// Helper function to get position of an artist in results
	const getArtistPosition = useCallback(
		(artistId: string) => {
			const artistIndex = artistResults.findIndex(
				(a: ArtistSearchResult) => a.id === artistId,
			);
			if (artistIndex === -1) return -1;
			// Artists come after festivals and shows
			return festivalResults.length + showResults.length + artistIndex;
		},
		[artistResults, festivalResults, showResults],
	);

	// Handle user selection
	const handleSelectUser = useCallback(
		(username: string) => {
			// Track search result click
			const position = getUserPosition(username);
			if (position !== -1) {
				trackSearchResultClick("user", position, searchValue);
			}

			resetSearchState();
			// Always navigate directly to public profile
			window.location.href = `/${username}`;
		},
		[resetSearchState, searchValue, getUserPosition],
	);

	// Handle festival selection
	const handleSelectFestival = useCallback(
		(festivalSlug: string) => {
			// Track search result click
			const position = getFestivalPosition(festivalSlug);
			if (position !== -1) {
				trackSearchResultClick("festival", position, searchValue);
			}

			resetSearchState();
			// Use navigation callback if provided, otherwise use window.location
			if (onNavigateToFestival) {
				onNavigateToFestival(festivalSlug);
			} else {
				window.location.href = `/festivals/${festivalSlug}`;
			}
		},
		[onNavigateToFestival, resetSearchState, searchValue, getFestivalPosition],
	);

	// Handle artist selection
	const handleSelectArtist = useCallback(
		(artist: ArtistSearchResult) => {
			// Track search result click
			const position = getArtistPosition(artist.id);
			if (position !== -1) {
				trackSearchResultClick("artist", position, searchValue);
			}

			// Track artist discovery from search
			trackArtistDiscovery(artist.id, artist.name, "search", "viewed");

			resetSearchState();

			// Route to group page for group aliases, artist page for regular artists
			let url: string;
			if (artist.is_group_alias && artist.group_slug) {
				url = `/groups/${encodeURIComponent(artist.group_slug)}?from=search`;
			} else {
				const artistSlug = createArtistSlug(artist.name, artist.slug);
				url = `/artists/${artistSlug}?from=search`;
			}
			window.location.href = url;
		},
		[resetSearchState, searchValue, getArtistPosition],
	);

	// Handle show selection
	const handleSelectShow = useCallback(
		(show: ShowSearchResult) => {
			// NOTE: Show tracking not yet implemented in analytics
			// Uncomment when trackSearchResultClick supports "show" type:
			// const position = getShowPosition(show.slug)
			// if (position !== -1) {
			//   trackSearchResultClick("show", position, searchValue)
			// }

			resetSearchState();
			window.location.href = `/shows/${show.slug}?from=search`;
		},
		[resetSearchState],
	);

	// Listen for "/" or "Cmd+K" keys to open search
	useEffect(() => {
		const handleSearchKeys = (e: KeyboardEvent) => {
			// Ignore if already open or if user is typing in an input
			if (
				isSearchOpen ||
				e.target instanceof HTMLInputElement ||
				e.target instanceof HTMLTextAreaElement ||
				(e.target instanceof HTMLElement && e.target.isContentEditable)
			) {
				return;
			}

			// Handle "/" key
			if (e.key === "/" || e.key === "Slash") {
				e.preventDefault();
				setSearchOpen(true);
			}

			// Handle "Cmd+K" (Mac) or "Ctrl+K" (Windows/Linux)
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setSearchOpen(true);
			}
		};

		window.addEventListener("keydown", handleSearchKeys);
		return () => window.removeEventListener("keydown", handleSearchKeys);
	}, [isSearchOpen, setSearchOpen]);

	return (
		<>
			<button
				type="button"
				onClick={() => setSearchOpen(true)}
				className={cn(
					"relative flex h-8 w-8 items-center justify-center rounded-full outline-none transition-all duration-200",
					"hover:ring-4 hover:ring-primary/10 active:scale-95 active:ring-primary/20",
					"focus-visible:ring-2 focus-visible:ring-primary/30",
				)}
				aria-label="Search"
			>
				<RiSearchLine className="h-4 w-4" />
			</button>

			<SearchDialog
				isOpen={isSearchOpen}
				onClose={() => resetSearchState()}
				searchValue={searchValue}
				onSearchValueChange={setSearchValue}
				selectedIndex={selectedSearchIndex}
				onSelectedIndexChange={setSelectedSearchIndex}
				userResults={userResults}
				festivalResults={festivalResults}
				showResults={showResults}
				artistResults={artistResults}
				isLoading={isLoading}
				error={error ? String(error) : null}
				onSelectUser={handleSelectUser}
				onSelectFestival={handleSelectFestival}
				onSelectShow={handleSelectShow}
				onSelectArtist={handleSelectArtist}
			/>
		</>
	);
}
